import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { Logo } from "./Navbar";
import { useLang } from "../../lib/LangContext";
import { openCookieSettings } from "../../lib/consent";
import { COMPANY, isFilled } from "../../data/legal";

const cols = [
  {
    title: "Product",
    links: [
      ["How It Works", "/how-it-works"],
      ["Services", "/services"],
      ["Examples", "/examples"],
      ["Pricing", "/pricing"],
      ["Get Started", "/get-started"],
    ],
  },
  {
    title: "Support",
    links: [
      ["FAQ", "/faq"],
      ["Contact", "/contact"],
      ["Login", "/login"],
      ["Trust & Security", "/legal/trust-security"],
      ["Legal Notice", "/legal/legal-notice"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy Policy", "/legal/privacy-policy"],
      ["Cookie Policy", "/legal/cookie-policy"],
      ["Terms of Service", "/legal/terms"],
      ["Acceptable Use Policy", "/legal/acceptable-use"],
      ["Data Processing Agreement", "/legal/dpa"],
      ["Refund & Cancellation", "/legal/refund-policy"],
    ],
  },
];

export default function Footer() {
  const { tr } = useLang();
  const registration = [
    isFilled(COMPANY.registeredName) ? COMPANY.registeredName : null,
    isFilled(COMPANY.kvk) ? `KVK ${COMPANY.kvk}` : null,
    isFilled(COMPANY.vat) ? `VAT ${COMPANY.vat}` : null,
    isFilled(COMPANY.registeredAddress) ? COMPANY.registeredAddress : null,
  ].filter(Boolean);

  return (
    <footer data-testid="site-footer" className="mt-24 border-t border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[1.4fr_repeat(3,1fr)] lg:px-8">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
            {tr("Your social media. Almost on autopilot. Create, plan, approve and publish your content from one place.")}
          </p>
          <p className="mt-4 text-sm text-slate-500">
            <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="hover:text-[#F95738]">
              {COMPANY.email}
            </a>
          </p>
          <div className="mt-6 flex items-center gap-3">
            {[
              [Instagram, "Instagram", "https://instagram.com"],
              [Facebook, "Facebook", "https://facebook.com"],
              [Linkedin, "LinkedIn", "https://linkedin.com"],
            ].map(([Icon, label, href]) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        {cols.map((col) => (
          <div key={col.title}>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-900">{tr(col.title)}</h4>
            <ul className="mt-4 space-y-2.5">
              {col.links.map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-slate-500 transition-colors hover:text-[#F95738]"
                  >
                    {tr(label)}
                  </Link>
                </li>
              ))}
              {col.title === "Legal" && (
                <li>
                  <button
                    type="button"
                    onClick={openCookieSettings}
                    data-testid="footer-cookie-settings"
                    className="text-sm text-slate-500 transition-colors hover:text-[#F95738]"
                  >
                    {tr("Cookie settings")}
                  </button>
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-200 px-5 py-8 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} PostMint. {tr("All rights reserved.")}</p>
          <p data-testid="footer-registration">
            {registration.length > 0 ? (
              registration.join(" · ")
            ) : (
              <>
                {tr("Registered in the Netherlands")} ·{" "}
                <Link to="/legal/legal-notice" className="hover:text-[#F95738]">
                  {tr("Company details")}
                </Link>
              </>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}
